
export enum AppMode { DISPATCHER = 'DISPATCHER', DRIVER_LOGIN = 'DRIVER_LOGIN', DRIVER_DASHBOARD = 'DRIVER_DASHBOARD' }
export type ThemeMode = 'dark' | 'solar';
export type UserRole = 'admin' | 'dispatcher' | 'driver';
export interface UserProfile { uid: string; email: string; role: UserRole; storeId: string; associatedDriverId?: string; }

/**
 * v46.0: Order Interface - Strategic Intelligence
 */
export interface Order {
    id: string;
    platformId?: string;
    displayId?: string;
    shortCode?: string;
    customerName: string;
    phone?: string;
    address: string;
    streetAddress: string;
    addressNumber?: string; // v46.6: Número do endereço separado
    neighborhood: string;
    city?: string;
    state?: string;
    complement?: string;
    reference?: string;
    items: string;
    orderValue: number;
    deliveryFee: number;
    paymentMethod: string;
    timeLimit: number;
    createdAt: number;
    salesChannel: string;
    type: 'DELIVERY' | 'TAKEOUT';
    status: 'pending' | 'waiting_confirmation' | 'scheduled_confirmed' | 'payment_pending' | 'scheduled' | 'confirmed' | 'ready' | 'assigned' | 'dispatched' | 'dispatched_3rd_party' | 'delivered' | 'returned' | 'canceled' | 'archived' | 'closed';
    orderTiming?: 'immediate' | 'scheduled';
    scheduledStartAt?: number;
    scheduledEndAt?: number;
    costThirdParty: number;
    lat?: number; lng?: number;
    isRoadDistance?: boolean;
    geocodingFailed?: boolean;
    isLoading?: boolean;
    isReady: boolean;
    driverId?: string;
    completedDriverId?: string;
    pickupConfirmed?: boolean;
    statusUpdatedAt?: number;
    distance?: number;
    estimatedDuration?: number;
    source?: 'OPEN_DELIVERY' | 'CARDAPIO_WEB' | 'MANUAL';
    ghostDispatch?: boolean;
    ghostDispatchTriggerTime?: number;
    ghostDispatchSent?: boolean;
    smartClusterId?: string;
    clusterColor?: string;
    routeSequence?: number;
    optimizationOutcome?: 'own' | 'third';
    manualAssignment?: 'own' | 'third';
    manualRouteGroup?: string;
    manualAssignmentReason?: string; // Motivo da atribuição manual para auditoria
    isPriority?: boolean;
    isManuallyAdjusted?: boolean;
    isHeldForBatching?: boolean;
    externalCallConfirmed?: boolean;
    externalDeliveryId?: string;
    externalLastEvent?: string;
    externalLastEventAt?: string;
    externalDispatchAttempts?: {
        at: number;
        providerName: string;
        result: 'success' | 'failed';
        message?: string;
    }[];
    externalDispatchLastError?: string;
    externalDispatchLastErrorAt?: number;
    isBoomerangReserved?: boolean;
    boomerangPhase?: 'go' | 'return';
    divergentStrategy?: 'production_retention' | 'dispatch_hold' | 'boomerang';
    divergentStrategyPhase?: 'holding' | 'go' | 'return';
    strategyConfirmedAt?: number;
    dispatchRequestedAt?: number;
    incidents?: any[];
    needsConfirmation?: boolean;
    deliveredAt?: number;
    pickupCode?: string;
    pendingCancellationRequest?: boolean;
    isManualCost?: boolean;
    bestProvider?: string;
    pickupConfirmedAt?: number; // v6.2: Timestamp de confirmação de saída
    streetViewPanoId?: string; // v35.1: Cache de Street View
    streetViewHeading?: number;
    reconLat?: number;
    reconLng?: number;
    slackTime?: number;
    departedHubAt?: number; // v6.1: Telemetria de saída
    arrivedAt?: number;    // v6.1: Telemetria de chegada no cliente

    // v46.0: Estratégias Rejeitadas para Independência Lógica
    ignoredStrategies?: string[];
    acceptedStrategies?: string[];
    ifoodSupportId?: string; // v46.1: ID de 8 dígitos para confirmação/central iFood
    customerProxyPhone?: string; // v47.0: Telefone mascarado (Proxy) do iFood
    calculatedOwnFleetCost?: number; // v7.6: Custo calculado para auditoria de ajuste manual
    serviceTimeMins?: number; // v56.11: Tempo real de atendimento na porta (em minutos)
}

export interface Driver {
    id: string;
    name: string;
    status: 'active' | 'inactive' | 'offline';
    isAvailable: boolean;
    currentLat?: number;
    currentLng?: number;
    lat?: number; // v2.1: Alias for currentLat
    lng?: number; // v2.1: Alias for currentLng
    lastUpdate?: number;
    phone?: string;
    vehicleType?: 'moto' | 'car' | 'bike';
    appVersion?: string;
    batteryLevel?: number;
    battery?: number; // v2.1: Alias for batteryLevel
    totalDeliveries?: number; // v10.2: Contador acumulado/legado
    dailyDeliveriesDate?: string; // YYYY-MM-DD local do contador diário persistido
    dailyDeliveriesCount?: number; // fallback persistido para app do motoboy quando RLS oculta pedidos concluídos
    cashReturns?: any[]; // v10.5: Para controle de sangrias
    // v46.0: Limite de Raio de Atuação
    maxRangeKm?: number;
    speedFactor?: number;
    maxDistance?: number;
    avgSpeedKmH?: number;
    avgServiceTimeMinutes?: number; // v6.1: Tempo médio na porta do cliente
    schedule?: DriverSchedule;
}

export interface DriverSchedule {
    activeDays: number[];
    shifts: { start: string; end: string }[];
}

export interface LogisticsProfile {
    maxOrdersPerRoute: number;
    clusteringMaxRadiusKm: number;
    clusteringMaxAngleDegrees: number;
    clusteringMinDistanceForAngleCheck: number;
    zigzagIgnoreTimeMinutes: number;
    serviceTimeMinutes: number;
    slaToleranceMinutes: number;
    // Margem fixa adicional (B) para validação de SLA do Bumerangue
    boomerangSafetyMarginMinutes?: number;
    // Ganho mínimo de ociosidade em R$ (epsilon) para justificar Bumerangue
    boomerangMinIdleSavings?: number;
    dailyFixedCost?: number;
    averageShiftHours?: number;
    driverFixedFeePerOrder?: number;
    batchDiscountPercentage?: number;
    // v56.11: Sobretaxas de contexto para apps terceiros
    rainSurchargeLight?: number;
    rainSurchargeHeavy?: number;
    trafficRiskPercentage?: number;
}

export interface RoutingOsrmCalibrationConfig {
    enabled: boolean;
    shortTripMaxDistanceKm: number;
    mediumTripMaxDistanceKm: number;
    shortTripMultiplier: number;
    mediumTripMultiplier: number;
    longTripMultiplier: number;
    fixedDelayMinutes: number;
    peakHourEnabled: boolean;
    peakHourMultiplier: number;
    peakMorningStartHour: number;
    peakMorningEndHour: number;
    peakEveningStartHour: number;
    peakEveningEndHour: number;
}

/**
 * v2025.02.14: Configuração do Monitor de Retenção
 * Controla os triggers de liberação para estratégias divergentes
 */
export interface RetentionMonitorConfig {
    // Production Retention
    productionMarginMinutes: number;      // Margem segurança (default: 3 min)
    
    // Dispatch Hold  
    dispatchHoldToleranceMulti: number;   // Tolerância extra para +1 pedido (default: 10 min)
    dispatchHoldMarginMinutes: number;    // Margem segurança (default: 3 min)
    
    // Comportamento
    checkIntervalMs: number;              // Intervalo de checagem (default: 10000ms = 10s)
    warnCooldownMs: number;               // Tempo mínimo entre avisos (default: 60000ms = 1min)
}

/**
 * Modo de recomendacao logistica.
 * - `advanced`: estrategia completa atual com arbitragem multi-sinal e estrategias divergentes.
 * - `simplified`: estrategia deterministica e explicavel, com regras operacionais fixas.
 */
export type RoutingRecommendationMode = 'advanced' | 'simplified';

export interface SystemConfig {
    storeName?: string;
    businessAddress?: string;
    storePhone?: string;
    businessLat: number;
    businessLng: number;
    maxDeliveryRadiusKm?: number;
    googleMapsApiKey: string;
    // v2025.02.08: HERE Maps API Key for primary routing provider
    hereMapsApiKey?: string;
    streetViewHeading?: number;
    streetViewRadius?: number;
    operatingHours?: any;
    averageKitchenTime: number;
    driversCollectPayments?: boolean;
    shopWhatsApp?: string;
    rainRiskPercentage?: number;
    rainSurchargeLight?: number;
    rainSurchargeHeavy?: number;
    trafficRiskPercentage?: number;
    dailyFixedCost?: number;
    averageShiftHours?: number;
    driverFixedFeePerOrder?: number;
    // v48.5: Configuração de Drenagem e Inteligência
    drainageThresholdMinutes?: number;
    kitchenRollingWindowSize?: number;
    costPerMinuteLowDemand?: number;
    costPerMinuteHighDemand?: number;
    loadFactorThreshold?: number;
    cardapioWeb?: CardapioWebConfig;
    fleetConfig: LogisticsProfile;
    thirdPartyConfig: LogisticsProfile;
    thirdPartyDeviationThreshold?: number;
    thirdPartySmartRouteDiscount?: number;
    thirdPartyMinSavings?: number;
    enableThirdPartyDeviation?: boolean;
    enableThirdPartySmartRouteDiscount?: boolean;
    enableThirdPartyMinSavings?: boolean;
    openDelivery?: OpenDeliveryConfig; // DEPRECATED: Moved to DeliveryProvider
    openDeliveryIdentity?: {
        merchantId: string;
        merchantSecret: string;
    };
    oracle?: OracleConfig;
    communication?: CommunicationConfig;
    printerConfig?: any;
    autoPilotEnabled?: boolean;
    isEconomicMode?: boolean;
    isSolarMode?: boolean;
    sofiaPersonalityPrompt: string;
    geminiApiKey?: string;
    hgWeatherApiKey?: string;
    maxFreeTtsCharacters?: number;
    simulatedTtsCharactersUsed?: number;
    lastTtsUsageResetDate?: string; // v56.11: Data do último reset do contador TTS
    // v2025.02.08: Configuração de API de Roteamento
    routingApiProvider?: 'here' | 'osrm' | 'google' | 'auto';
    routingApiFallbackEnabled?: boolean;
    routingOsrmCalibration?: RoutingOsrmCalibrationConfig;
    // v2025.02.14: Enhanced routing configuration with provider preference
    routingConfig?: {
        provider?: 'here' | 'google' | 'osrm' | 'auto';
        fallbackEnabled?: boolean;
        useTraffic?: boolean;
    };
    // v2025.02.14: Configuração do Monitor de Retenção (triggers de liberação)
    retentionMonitorConfig?: RetentionMonitorConfig;
    /**
     * Seletor interno de estrategia de recomendacao.
     * Este campo existe para ativacao operacional/feature-flag e nao exige mudancas no modal de configuracoes.
     */
    routingRecommendationMode?: RoutingRecommendationMode;
}

/**
 * v2025.02.08: HERE Routing Configuration
 * Configuration for routing provider selection and fallback behavior
 */
export interface HereRoutingConfig {
    provider: 'here' | 'google' | 'osrm';
    fallbackEnabled: boolean;
    hereApiKey?: string;
    googleApiKey?: string;
}

export interface OpenDeliveryConfig {
    active: boolean;
    mode: 'simulation' | 'production' | 'real'; // v56.11: Added 'real' for compatibility
    clientId: string;
    clientSecret: string;
    merchantId?: string; // Legacy/Manual override
    remoteMerchantId?: string; // NEW: The ID of this store in the Partner's system
    partnerWebhookUrl?: string; // NEW: The Webhook URL provided by the Partner
    baseUrl: string;
    callMode?: 'immediate' | 'delayed'; // v56.11: Made optional for compatibility
    delayMinutes?: number;
    pollingIntervalSeconds?: number; // v56.11: Added for polling config
    salAppId?: string; // v56.11: SAL App identifier
    webhookMode?: 'standard' | 'inverted'; // NEW: 'standard' = partner calls SAL webhooks, 'inverted' = SAL calls partner webhook
}

export interface CardapioWebConfig {
    active: boolean;
    proxyBaseUrl?: string;
    merchantId?: string;
    apiToken?: string;
    pollingIntervalSeconds?: number;
    scheduledRoutingLeadMinutes?: number;
    enableGhostDispatch: boolean;
    ghostDispatchMode: 'dynamic' | 'predictive';
    ghostDispatchDynamicMinSeconds?: number;
    ghostDispatchDynamicMaxSeconds?: number;
    ghostDispatchBufferMinutes?: number;
}

export type DecisionPolicyProfileId = 'sla-first' | 'balanced' | 'margin-first';

export interface DecisionScoringWeights {
    slaPriority: number;
    marginPriority: number;
    capacityPriority: number;
}

export interface DecisionPolicyProfile {
    id: DecisionPolicyProfileId;
    label: string;
    description: string;
    scoringWeights: DecisionScoringWeights;
}

export interface OracleConfig {
    active: boolean;
    hourlyShare: number[];
    dailyWeights: Record<number, number>;
    averageDailyOrders: number;
    isManualOverride: boolean;
    preservationMode: 'proportional' | 'none';
    maxPreservationBonus: number;
    manualPreservationLevel: number;
    calibratedAt?: number;
    calibratedHourlyShare?: number[];
    calibratedDailyWeights?: Record<number, number>;
    decisionPolicyProfile?: DecisionPolicyProfileId;
    decisionScoringWeights?: DecisionScoringWeights;
}

export interface PricingRule {
    maxDistance: number;
    price: number;
}

export interface ConditionalPricingRule {
    id: string;
    active: boolean;
    name: string;
    type: 'day_of_week' | 'time_range';
    days?: number[];
    startTime?: string;
    endTime?: string;
    modifierType: 'percentage' | 'fixed_amount';
    value: number;
}

export interface DeliveryProvider {
    id: string;
    name: string;
    color?: string;
    active: boolean;
    priority: number;
    strategy: 'flat' | 'per_km' | 'tiered';
    baseFee: number;
    pricePerKm?: number;
    tiers?: PricingRule[];
    conditions?: ConditionalPricingRule[];
    // Open Delivery Integration
    integrationType?: 'manual' | 'open_delivery';
    openDeliveryConfig?: OpenDeliveryConfig;
}

export interface OperatingContext {
    rainMode: 'off' | 'light' | 'heavy';
    isHighTraffic: boolean;
    surgeMultiplier: number;
    weather?: any;
    radar?: any;
}

export interface TimingGuidance {
    status: 'optimal' | 'critical' | 'waiting_kitchen' | 'waiting_batch' | 'production_retention';
    label: string;
    reason: string;
    waitMinutes?: number;
}

export type ServiceTimeObservationSource = 'explicit_order_metric' | 'arrived_delivered_delta';

export interface ServiceTimeObservation {
    orderId: string;
    driverId?: string;
    serviceTimeMinutes: number;
    observedAt?: number;
    source: ServiceTimeObservationSource;
}

export interface ServiceTimeMetricsQuery {
    driverId?: string;
    nowMs?: number;
    freshWindowDays?: number;
    staleAfterDays?: number;
}

export interface ServiceTimeMetricsSnapshot {
    history: number;
    last30Days: number;
    today: number;
    countToday: number;
    sampleCount: number;
    freshSampleCount: number;
    lastObservationAt?: number;
    confidence: number;
    isStale: boolean;
}

export type ServiceTimeResolutionSource =
    | 'driver_real_data'
    | 'driver_profile_fallback'
    | 'fleet_recent_data'
    | 'profile_default_fallback';

export interface ServiceTimeResolution {
    minutes: number;
    source: ServiceTimeResolutionSource;
    confidence: number;
    sampleCount: number;
    lastObservationAt?: number;
    rationale: string;
}

export interface SafeDispatchAnalysis {
    value: number;
    deadline: number;
    elapsed: number;
    travel: number;
    kitchen: number;
    service: number;
    tolerance: number;
    serviceSource?: ServiceTimeResolutionSource;
    serviceConfidence?: number;
    serviceSampleCount?: number;
    serviceLastObservationAt?: number;
    serviceRationale?: string;
}

export interface SafeDispatchSequencedStop {
    orderId: string;
    stopIndex: number;
    order: Order;
    legTravelMinutes: number;
    cumulativeTravelMinutes: number;
    cumulativeServiceMinutes: number;
    cumulativeRouteMinutes: number;
    analysis: SafeDispatchAnalysis;
}

export type DecisionReason = 'CHEAPER' | 'CONSTRAINT_NO_FLEET' | 'CAPACITY_PRESSURE' | 'SLA_PRIORITY' | 'SLA_E_ACTIVE' | 'RETENTION_STRATEGY' | 'UNKNOWN';

export interface DecisionTraceEntry {
    kind: 'route' | 'strategy';
    mode: 'own' | 'third' | 'hold';
    decisionReason?: DecisionReason;
    orderIds: string[];
    chosenLabel: string;
    rationale: string;
    ownCost?: number;
    thirdCost?: number;
    slackMin?: number;
    driverDelayMin?: number;
    constraints?: string[];
}

export interface OracleState {
    predictedPeakTime: number; // Timestamp of the next predicted peak
    surgeProbability: number; // 0-1 probability of surge
    currentLoadLevel: 'low' | 'medium' | 'high' | 'critical';
}

export interface KitchenMetrics {
    effectivePrepTime: number; // Moving average of prep time in minutes
    efficiencyRatio: number; // 1.0 = standard, >1.0 = slower
    currentQueueSize: number;
}


export interface SmartSplitSuggestion {
    ownFleet: Order[];
    thirdParty: Order[];
    heldOrders: Order[];
    oracleMultiplier?: number;
    routes: SuggestedRoute[];
    thirdPartyTripsCount: number;
    metrics: any;
    isSplitRecommended: boolean;
    optimizationConstrainedBySLA: boolean;
    estimatedTime: number;
    minSla: number;
    lowestSlack: number;
    waitSuggestion?: {
        message: string;
        waitTime: number;
    };
    sequentialOpportunity?: any;
    smartWaitAnalysis?: any;
    decisionTrace?: DecisionTraceEntry[];
    /** 
     * v59.0: Estratégias sugeridas aguardando confirmação do operador.
     * Diferente de heldOrders (pedidos já retidos), estas são sugestões
     * que ainda precisam ser confirmadas via modal Sofia.
     */
    suggestedStrategies?: {
        type: 'boomerang' | 'dispatch_hold' | 'production_retention';
        orders: Order[];
        savings: number;
        delayImpact: number;
        rationale: string;
        /** Se true, a estratégia foi confirmada e deve ser aplicada */
        isConfirmed?: boolean;
    }[];
}

export interface SuggestedRoute {
    type: 'own' | 'third';
    assigneeId?: string;
    assigneeName: string;
    orders: Order[];
    cost: number;
    strategyLabel?: string;
    decisionSummary?: string;
    isBoomerang?: boolean;
    timing?: TimingGuidance;
    requiresOvertimeApproval?: boolean; // v57.9: Alerta de hora extra
    estimatedReturnMinutes?: number;
}

// v2.4: Communication and Hailing Types
export interface HailingStrategy {
    method: 'text' | 'audio';
    content: string;
    reasoning: string;
    suggestedAudioClip?: AudioClip;
}

export interface AudioClip {
    id: string;
    name: string;
    scenario: string;
    base64Data: string;
    mimeType: string;
}

export interface TextClip {
    id: string;
    scenario: string;
    content: string;
}

export interface SofiaVoiceConfig {
    liveVoiceName: string;
    ttsVoiceName: string;
    proactiveQuality: 'gemini-tts' | 'native-tts';
    selectedVoiceQuality: 'cloud' | 'local';
    budgetGuardVoiceFallback: boolean;
    cloudTtsVoiceName?: string;
}

export interface CommunicationConfig {
    active: boolean;
    textClips: TextClip[];
    dispatcherTextClips?: TextClip[]; // v3.5: Mensagens do Painel Admin
    audioClips: AudioClip[];
    scenarioPreferences: Record<string, 'text' | 'audio'>;
    enableAutoHailing?: boolean;
    hailingPersonalityPrompt?: string;
    sofiaVoiceConfig?: SofiaVoiceConfig;
}

export interface Incident {
    id: string;
    timestamp: number;
    type: string;
    description: string;
    severity: 'low' | 'medium' | 'high';
    reportedBy: string;
    valueImpact?: number; // v56.11: Impacto financeiro do incidente
    resolved?: boolean; // v56.11: Status de resolução do incidente
}

// v56.11: Alias de tipo para compatibilidade
export type IncidentType = 'delay' | 'damage' | 'wrong_address' | 'customer_complaint' | 'other' | 'refusal' | 'wrong_item' | 'address_issue';

// v56.11: Snapshot financeiro para relatórios
export interface FinancialSnapshot {
    revenue: number;
    costs: number;
    profit: number;
    orderCount: number;
    periodStart: number;
    periodEnd: number;
    // v56.11: Campos adicionais usados em CashFlowModal
    grossProducts?: number;
    grossFees?: number;
    revenueTotal?: number;
    revenueCash?: number;
    revenueOnline?: number;
    revenueCard?: number;
    // v56.11: Campos de custo retornados por calculateFinancials
    costFleetFixed?: number;
    costFleetVariable?: number;
    costIdleCapacity?: number;
    costThirdParty?: number;
    costIncidents?: number;
    netProfit?: number;
    cashInHandExpected?: number;
    avgCostPerOwnDelivery?: number;
}

export interface WebhookPayload {
    source: string;
    event: string;
    data: any;
    storeIdentifier?: string;
}
